/**
 * EL CARA A CARA CONTRA UNA PERSONA.
 *
 * QUÉ PREGUNTA CONTESTA. «¿Cómo me va contra este?» — la única pregunta que
 * un jugador se hace antes de aceptar un reto, y la que ninguna otra pantalla
 * contestaba: el historial del perfil trae TODOS sus partidos, contra todos,
 * y encontrar los tres que jugamos entre nosotros era leer treinta.
 *
 * CUATRO NÚMEROS Y NO UNO. «3-3» no dice nada: se puede empatar en partidos
 * habiendo ganado el doble de games, o al revés. Partidos, sets, games y
 * desempates cuentan cuatro historias distintas, y la de los desempates es la
 * que la gente busca de verdad — quién aguanta cuando se pone parejo.
 *
 * LO MÍO SIEMPRE A LA IZQUIERDA. En toda la pantalla, sin excepción: una
 * tabla que a veces pone primero al que mira es cómo alguien lee el
 * porcentaje del rival como si fuera el propio.
 *
 * LOS PORCENTAJES SE CALCULAN ACÁ Y NO EN EL SERVIDOR: son dos divisiones
 * sobre números que ya viajaron, y mandarlos sería mandar el mismo hecho dos
 * veces con la posibilidad de que no coincidan.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { PLAYED_MATCH_KINDS, rivalryOf } from '@tennisup/contracts';
import type { PlayedMatch, PlayedMatchKind, Rivalry, RivalryTally } from '@tennisup/contracts';

import { Alert } from '../components/Alert';
import { AppLayout } from '../components/AppLayout';
import { Avatar } from '../components/Avatar';
import { BackButton } from '../components/BackButton';
import { Paginador, usePaginado } from '../components/Paginado';
import { useAuth } from '../auth/AuthContext';
import { api, assetUrl } from '../lib/api';
import { rutaDelPartido } from '../lib/activityActions';
import { errorMessage } from '../lib/errorMessage';

export function RivalryPage(): JSX.Element {
  const { t } = useTranslation();
  const { session } = useAuth();
  /*
   * DOS FORMAS DE LLEGAR: `/jugadores/:id/vs` es «yo contra ese», y
   * `/jugadores/:id/vs/:otherId` es entre dos personas cualesquiera. Es la
   * misma pantalla porque es la misma pregunta; lo único que cambia es quién
   * va de cada lado.
   */
  const { id = '', otherId } = useParams();
  const entreTerceros = otherId !== undefined && otherId !== '';
  const [rivalidad, setRivalidad] = useState<Rivalry | null>(null);
  const [listo, setListo] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setRivalidad(
        entreTerceros ? await api.rivalryBetween(id, otherId) : await api.playerRivalry(id),
      );
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      // `listo` y no «rivalidad !== null»: nunca haberse enfrentado TAMBIÉN
      // es una respuesta, y sin esta bandera se quedaría en «Cargando…».
      setListo(true);
    }
  }, [id, otherId, entreTerceros, t]);

  useEffect(() => {
    void load();
  }, [load]);

  /*
   * QUIÉN VA DE ESTE LADO. Entre dos terceros NO soy yo, y poner mi nombre
   * —o «Vos»— sobre los partidos de otra persona sería mentir en la línea más
   * visible de la pantalla. El nombre del primero viene en la respuesta, pero
   * del lado del RIVAL (`opponentName`): acá se arma al revés, así que
   * mientras no llegue se deja vacío y la cuenta igual se lee.
   */
  const yo = entreTerceros
    ? (rivalidad?.subjectName ?? '')
    : (session?.user.displayName ?? t('rivalry.you'));

  return (
    <AppLayout>
      <BackButton to={`/jugadores/${id}`} />
      {/*
        EL TÍTULO DICE «vs N», NO «Cara a cara».
        Javier: «no deberia existir el texto cara a cara mas bien deberia ser
        vs N». Es el mismo cambio que ya se hizo en las tarjetas del muro y de
        la actividad, terminado acá.

        EL NÚMERO ES EL TOTAL SIN FILTRAR, a propósito: el de abajo
        (`rivalry.played`) sigue los filtros de clase de partido y baja cuando
        se desmarca «amistoso». Un título que cambia mientras alguien toca los
        filtros deja de ser el nombre de la pantalla. Cuántos son de cada clase
        lo dice el renglón de abajo, que es su trabajo.

        MIENTRAS CARGA VA SIN NÚMERO: poner «vs 0» antes de que llegue la
        respuesta afirma que nunca jugaron, que es justo lo contrario de lo
        que puede resultar cierto un segundo después.
      */}
      <h1>
        {rivalidad == null
          ? t('rivalry.between')
          : t('challenges.playedTogether', { count: rivalidad.played })}
      </h1>

      {error !== null && <Alert kind="error">{error}</Alert>}

      {!listo ? (
        error === null ? <p className="help">{t('common.loading')}</p> : null
      ) : /* `== null` FLOJO A PROPÓSITO: atrapa el nulo y el indefinido de una.
             La frontera ya normaliza el cuerpo vacío a `null` (ver `request`),
             así que esto es la segunda barrera, no la primera — pero es la que
             hace que la pantalla no se caiga si alguna vez vuelve a llegar un
             indefinido por otro camino. Una pantalla en blanco no se puede
             leer ni reportar: no dice qué pasó ni qué hacer. */
      rivalidad == null ? (
        // NUNCA JUGARON. Se dice con palabras y no con un «0-0», que se lee
        // como un empate y no como una ausencia.
        <p className="help">{t('rivalry.none')}</p>
      ) : (
        <Cuentas
          rivalidad={rivalidad}
          yo={yo}
          avatar={entreTerceros ? (rivalidad.subjectAvatarPath ?? null) : (session?.user.avatarPath ?? null)}
        />
      )}
    </AppLayout>
  );
}

function Cuentas({
  rivalidad,
  yo,
  avatar,
}: {
  rivalidad: Rivalry;
  yo: string;
  avatar: string | null;
}): JSX.Element {
  const { t } = useTranslation();
  /*
   * `?? []` Y NO `rivalidad.history` A SECAS.
   *
   * El contrato dice que `history` siempre viene, y el servidor la manda
   * siempre. Pero un tipo es una PROMESA SOBRE EL CONTRATO, no una garantía
   * sobre el cable, y en esta misma aplicación ya pasó tres veces que un
   * campo que «siempre viene» llegó vacío —los patrocinadores fueron el
   * último—. Lo que se pierde con esta línea es nada; lo que se evita es que
   * la pantalla entera desaparezca, que es el peor final posible: no dice qué
   * pasó, no se puede reportar, y quien la ve cree que la aplicación murió.
   */
  /*
   * QUÉ CLASES SE ESTÁN MIRANDO. Vacío = todas.
   *
   * SE FILTRA ACÁ Y NO EN EL SERVIDOR, y no es por ahorrar un viaje: es para
   * que tocar un chip conteste al instante. Los partidos ya viajaron enteros;
   * pedirlos de nuevo por cada toque sería esperar medio segundo para volver
   * a recibir la misma lista con menos filas.
   */
  const [clases, setClases] = useState<readonly PlayedMatchKind[]>([]);

  /*
   * Y SE VUELVE A CONTAR CON LA MISMA FUNCIÓN QUE USÓ EL SERVIDOR.
   *
   * `rivalryOf` vive en el contrato justamente para esto. Escribir acá una
   * segunda suma de sets y games —«total filtrado»— sería tener dos cuentas
   * del mismo hecho, y el día que una cambie, el número de arriba va a
   * contradecir a la lista de abajo sin que nadie sepa cuál creer.
   */
  const filtrada = useMemo(
    () => rivalryOf(rivalidad.history ?? [], rivalidad.opponentUserId, clases) ?? rivalidad,
    [rivalidad, clases],
  );

  function alternar(clase: PlayedMatchKind): void {
    setClases((antes) =>
      antes.includes(clase) ? antes.filter((c) => c !== clase) : [...antes, clase],
    );
  }

  const pagina = usePaginado(filtrada.history ?? [], clases.join('|'));

  return (
    <>
      <div className="vs-head">
        <span className="vs-side">
          <Avatar url={assetUrl(avatar)} name={yo} size={48} />
          <span>{yo}</span>
        </span>
        {/* «VS» sin traducir, y no por descuido: es la abreviatura que se usa
            igual en las nueve pantallas y en cualquier cancha del mundo. El
            que sí se lee en voz alta es el título de la tabla, que va
            traducido en su `caption`. */}
        <span className="vs-word" aria-hidden="true">
          VS
        </span>
        <span className="vs-side">
          {/* La foto del rival no viaja con la rivalidad: son sus partidos, no
              su perfil. Las iniciales alcanzan, y es un viaje menos. */}
          {/* SU FOTO, que viaja con la rivalidad. Acá iba `url={null}` fijo,
              así que el rival salía siempre con sus iniciales aunque tuviera
              foto puesta — mi lado sí la mostraba, y la diferencia entre las
              dos columnas se leía como que el otro no tenía. */}
          <Avatar url={assetUrl(rivalidad.opponentAvatarPath)} name={rivalidad.opponentName} size={48} />
          <span>{rivalidad.opponentName}</span>
        </span>
      </div>

      <p className="help vs-count">
        {t('rivalry.played', { count: filtrada.played })}
        {/* LOS EMPATES APARTE, y solo si los hay. Un «0 empates» al lado de
            todo partido ocuparía un renglón para no decir nada, y además
            haría creer que el empate es común cuando solo puede salir de un
            amistoso. */}
        {filtrada.drawn > 0 && ` · ${t('rivalry.drawn', { count: filtrada.drawn })}`}
      </p>

      <FiltroDeClase byKind={rivalidad.byKind} elegidas={clases} onToggle={alternar} />

      <div className="table-scroll">
        <table className="data-table vs-table">
          <caption className="visually-hidden">
            {t('rivalry.vsTitle', { me: yo, them: rivalidad.opponentName })}
          </caption>
          {/*
            LOS DOS NOMBRES, ESCONDIDOS A LA VISTA Y PRESENTES PARA QUIEN
            ESCUCHA.

            Estaban escritos en el encabezado y eran lo que rompía la tabla:
            dos nombres completos en dos columnas de números la estiraban a
            más de lo que mide un teléfono, y la columna del rival quedaba
            cortada por el borde. Y estaban de más: los mismos dos nombres
            están arriba, grandes, con su foto, JUSTO ENCIMA de su columna.

            Se quedan en el HTML porque un lector de pantalla no ve ese
            «justo encima»: sin el encabezado, «3 de 6, 50 %» no se sabe de
            quién es.
          */}
          <thead>
            <tr>
              <th scope="col" className="num">
                <span className="visually-hidden">{yo}</span>
              </th>
              <th scope="col" className="vs-what">
                <span className="visually-hidden">{t('rivalry.title')}</span>
              </th>
              <th scope="col" className="num">
                <span className="visually-hidden">{rivalidad.opponentName}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <Fila que={t('rivalry.matches')} cuenta={filtrada.matches} />
            <Fila que={t('rivalry.sets')} cuenta={filtrada.sets} />
            <Fila que={t('rivalry.games')} cuenta={filtrada.games} />
            <Fila que={t('rivalry.tiebreaks')} cuenta={filtrada.tiebreaks} />
          </tbody>
        </table>
      </div>

      <section>
        <h2>{t('rivalry.history')}</h2>
        {/* CON UN FILTRO QUE NO DEJA NADA se dice por qué. Una lista vacía
            debajo de unos chips apretados se lee como que se rompió algo. */}
        {filtrada.history.length === 0 ? (
          <p className="help">{t('rivalry.noneOfThat')}</p>
        ) : (
          <ul className="vs-list">
            {pagina.visibles.map((partido) => (
              <Partido key={partido.matchId} partido={partido} />
            ))}
          </ul>
        )}
        <Paginador pagina={pagina.pagina} paginas={pagina.paginas} irA={pagina.irA} />
      </section>
    </>
  );
}

/**
 * LOS CHIPS DE ARRIBA: de qué clase mirar los partidos.
 *
 * Javier los pidió con la pantalla de MiniTour delante, donde son una fila de
 * chips con su cantidad al lado.
 *
 * SIN NINGUNO ELEGIDO SE VEN TODOS, y por eso el chip de «Todos» no es un
 * cuarto botón que se pueda apretar: es el estado en el que no hay ninguno
 * apretado. Un cuarto botón daría dos formas de decir lo mismo —«todos
 * apretados» y «ninguno apretado»— que además se pueden contradecir.
 *
 * SE PUEDEN ELEGIR VARIAS: «torneos y retos por ranking, sin amistosos» es
 * una pregunta razonable, y con radios habría que elegir de a una.
 *
 * UNA CLASE SIN PARTIDOS NO SE DIBUJA. Un chip en cero es un botón que solo
 * puede vaciar la lista, y quien lo toca cree que se rompió algo.
 */
function FiltroDeClase({
  byKind,
  elegidas,
  onToggle,
}: {
  byKind: Readonly<Record<PlayedMatchKind, number>>;
  elegidas: readonly PlayedMatchKind[];
  onToggle: (clase: PlayedMatchKind) => void;
}): JSX.Element | null {
  const { t } = useTranslation();
  const hay = PLAYED_MATCH_KINDS.filter((clase) => (byKind[clase] ?? 0) > 0);

  // Con una sola clase no hay nada que filtrar: el chip no cambiaría nada.
  if (hay.length < 2) return null;

  return (
    <div className="vs-kinds" role="group" aria-label={t('rivalry.filterKind')}>
      {hay.map((clase) => {
        const puesto = elegidas.includes(clase);
        return (
          <button
            key={clase}
            type="button"
            className={puesto ? 'vs-kind vs-kind-on' : 'vs-kind'}
            aria-pressed={puesto}
            onClick={() => onToggle(clase)}
          >
            {t(NOMBRE_DE_CLASE[clase])} <span className="vs-kind-count">{byKind[clase]}</span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Cómo se llama cada clase en pantalla.
 *
 * Las claves son las mismas que usa el historial del perfil: es el mismo
 * concepto dicho en dos pantallas, y dos juegos de textos para lo mismo es
 * cómo una termina diciendo «Amistoso» y la otra «Partido amistoso».
 */
const NOMBRE_DE_CLASE: Readonly<Record<PlayedMatchKind, string>> = {
  TOURNAMENT: 'matchHistory.kindTournament',
  CHALLENGE_RANKING: 'matchHistory.kindRanking',
  CHALLENGE_FRIENDLY: 'matchHistory.kindFriendly',
};

/**
 * Un renglón de la cuenta: lo mío, qué se está contando, lo del otro.
 *
 * EL PORCENTAJE VA AL LADO DE CADA NÚMERO y no en su propia columna: «7/15»
 * sin el 47 % obliga a dividir con la cabeza, y una quinta columna en un
 * teléfono deja las cinco ilegibles.
 *
 * CON CERO NO SE DIVIDE. «0 de 0» no es 0 %, es que no hay nada que
 * calcular, y un `NaN%` en una tabla de estadísticas es lo único que se lee
 * peor que ninguna estadística.
 */
function Fila({ que, cuenta }: { que: string; cuenta: RivalryTally }): JSX.Element {
  const total = cuenta.mine + cuenta.theirs;

  return (
    <tr>
      <td className="num">
        <Lado propio={cuenta.mine} total={total} />
      </td>
      <th scope="row" className="vs-what">
        {que}
      </th>
      <td className="num">
        <Lado propio={cuenta.theirs} total={total} />
      </td>
    </tr>
  );
}

function Lado({ propio, total }: { propio: number; total: number }): JSX.Element {
  return (
    <>
      <span className="vs-num">
        {propio}
        <span className="vs-total">/{total}</span>
      </span>
      {total > 0 && (
        <span className="vs-pct">{`${Math.round((propio / total) * 100)}%`}</span>
      )}
    </>
  );
}

/**
 * Un partido del historial.
 *
 * El marcador viene con MI número primero —ver `PlayedMatch`— así que se
 * escribe tal cual. Un walkover no tiene sets y se dice con la palabra: un
 * renglón en blanco donde va el marcador se lee como un dato que falta.
 */
function Partido({ partido }: { partido: PlayedMatch }): JSX.Element {
  const { t } = useTranslation();
  const marcador =
    partido.score.length === 0
      ? t('score.wo')
      : partido.score.map((set) => `${set.home}-${set.away}`).join(', ');

  // Dónde se jugó: un reto no tiene torneo, así que en su lugar va qué clase
  // de reto fue. Dejar el hueco se lee como un dato que falta.
  const dónde =
    partido.tournamentName ??
    t(
      partido.kind === 'CHALLENGE_RANKING'
        ? 'matchHistory.kindRanking'
        : 'matchHistory.kindFriendly',
    );

  const marca = partido.outcome === 'WON' ? '✓' : partido.outcome === 'DRAW' ? '=' : '✕';
  const cómoLeFue =
    partido.outcome === 'WON'
      ? t('score.winner')
      : partido.outcome === 'DRAW'
        ? t('matchHistory.drew')
        : t('score.loser');

  /*
   * LA FILA ENTERA ABRE EL PARTIDO: su foto, sus comentarios y sus
   * reacciones. Era una lista de resultados que no llevaba a ningún lado, y
   * lo que uno quiere al leer «6-3, 3-6, 6-4» es ver cómo fue.
   *
   * EL ENLACE ES LA FILA Y NO UN BOTÓN AL FINAL: en un teléfono, un blanco
   * del ancho de la pantalla se acierta sin mirar, y no le saca lugar a nada.
   */
  return (
    <li className={partido.outcome === 'WON' ? 'vs-row vs-won' : 'vs-row'}>
      <Link
        className="vs-open"
        to={rutaDelPartido(partido.kind, partido.matchId)}
        aria-label={t('matchHistory.open', { name: partido.opponentName })}
      >
        <span className="vs-when">
          <span>{dónde}</span>
          <span className="help">{partido.communityName ?? partido.playedOn}</span>
        </span>
        <span className="vs-score">
          <span className="vs-mark" aria-label={cómoLeFue}>
            <span aria-hidden="true">{marca}</span>
          </span>
          <span>{marcador}</span>
        </span>
      </Link>
    </li>
  );
}
